import { computed, ref, shallowRef, watch, watchEffect, type Ref } from 'vue'
import { ApiError } from '../api/client'

export interface AsyncState<T> {
  data: Ref<T | null>
  error: Ref<ApiError | null>
  loading: Ref<boolean>
  /** 首次加载；reload 时保持旧数据 */
  initial: Ref<boolean>
  reload: () => void
}

export function useAsync<T>(fn: () => Promise<T>, deps: () => unknown[] = () => []): AsyncState<T> {
  const data = shallowRef<T | null>(null) as Ref<T | null>
  const error = ref<ApiError | null>(null)
  const loading = ref(true)
  const initial = ref(true)
  const nonce = ref(0)

  watch(
    () => [...deps(), nonce.value],
    (_curr, _prev, onCleanup) => {
      let cancelled = false
      onCleanup(() => {
        cancelled = true
      })
      loading.value = true
      error.value = null
      void fn()
        .then((result) => {
          if (!cancelled) data.value = result
        })
        .catch((err: unknown) => {
          if (cancelled) return
          error.value = err instanceof ApiError ? err : new ApiError(0, 'unknown', '发生未知错误')
        })
        .finally(() => {
          if (cancelled) return
          loading.value = false
          initial.value = false
        })
    },
    { immediate: true },
  )

  const reload = () => {
    nonce.value += 1
  }

  return { data, error, loading, initial, reload }
}

export function useDebounced<T>(value: Ref<T>, delay = 300): Ref<T> {
  const debounced = ref(value.value) as Ref<T>
  let timer: ReturnType<typeof setTimeout> | undefined

  watch(value, (v) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      debounced.value = v
    }, delay)
  })

  return debounced
}

export function useTitle(title: Ref<string> | string) {
  const titleRef = typeof title === 'string' ? computed(() => title) : title
  watchEffect(() => {
    document.title = `${titleRef.value} · Reellax`
  })
}
